// Import global utils
import { getUniqueID } from '@automator/shared/utils/get-unique-id';
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

import Condition from '@automator/builder/_legacy/js/item/conditions/condition';
import ItemConditionsModalOptions from '@automator/builder/_legacy/js/item/conditions/modal-options';
import { getConditionsBlockNode } from '@automator/builder/_legacy/js/item/conditions/utilities';
import { shouldBeSortable } from '@automator/builder/_legacy/js/item/sortability/utilities';

/**
 * Creates a conditions group
 */
export default class ConditionsGroup {
	// Private properties
	// Mode. Use .mode
	#_mode = 'any';

	// Actions. Use .actions
	#_actions = [];

	// Conditions. Use .conditions
	#_conditions = {};

	// The priority. Use .priority
	#_priority;

	/**
	 * Creates an unique ID and adds the condition group to the main object
	 *
	 * @param {Object} options - Object with options
	 * @param {Integer} options.id - String with unique ID to target this conditions block
	 * @param {Integer} options.parentID - String with unique ID to target this conditions block
	 */
	constructor( { id, parentID } ) {
		// Get or create unique ID
		this.id       = ! isEmpty( id ) ? id : getUniqueID();

		// Set the parent ID. If the parent ID is not defined, it will return the recipe ID
		this.parentID = ! isEmpty( parentID ) ? parentID : UncannyAutomator._recipe.recipe_id;

		// Add it to the main object
		UncannyAutomator.recipe.conditions[ this.id ] = this;
	}

	/**
	 * Adds a condition
	 *
	 * @param {Object} options                 The options
	 * @param {String} options.conditionID     The unique ID of this condition (tied to the ConditionsBlock)
	 * @param {String} options.integrationCode The unique code of the integration
	 * @param {String} options.conditionCode   The unique code of the condition
	 * @param {Object} options.fields          The fields
	 * @param {Object} options.backup          The backed up data
	 * @return {Condition}                     The condition
	 */
	addCondition( { conditionID, integrationCode, conditionCode, fields, backup = {} } ) {
		// Create condition
		const condition = new Condition( { id: conditionID } );

		// Set the integration
		condition.integrationCode = integrationCode;

		// Set the condition code
		condition.conditionCode = conditionCode;

		// Set the fields data
		condition.fields = fields;

		// Set the backed up data
		// This is ONLY used if the condition is missing
		condition.backup = backup;

		// Add the ConditionGroup
		condition.conditionsGroup = this;

		// Add condition
		this.#_conditions[ condition.id ] = condition;

		// Send event
		this.sendEvent( 'automator/conditions-group/add-condition', {
			detail: {
				condition: condition
			},
		} );

		// Return the new condition
		return condition;
	}

	/**
	 * Removes a condition
	 *
	 * @param {Object} options             The options
	 * @param {String} options.conditionID The unique ID of the condition (NOT the code, the ID)
	 * @return {undefined}
	 */
	removeCondition( { conditionID } ) {
		// Check if the condition exists
		if ( ! isDefined( this.conditions[ conditionID ] ) ) {
			return;
		}

		// Remove it
		delete this.#_conditions[ conditionID ];

		// Send event
		this.sendEvent( 'automator/conditions-group/delete-condition', {
			detail: {
				conditionID: conditionID
			},
		})
	}

	/**
	 * Edits a condition
	 *
	 * @param {Object} options             The options
	 * @param {String} options.conditionId The unique ID of the condition (NOT the code, the ID)
	 * @return {undefined}
	 */
	editCondition( { conditionId } ) {
		// Create modal to edit condition
		new ItemConditionsModalOptions( {
			conditionsGroupId: this.id,
			conditionId: conditionId,
			comingFrom: 'edit-condition-pill',
		} );
	}

	/**
	 * Adds an action to the array of actions of this conditions group
	 *
	 * @param {Integer} actionID The action ID
	 */
	addAction( actionID ) {
		// Get the current list of actions
		const actions = this.actions;

		// Check if the current action wasn't already added
		if ( ! actions.includes( actionID ) ) {
			actions.push( actionID );
		}

		// Update
		this.actions = actions;

		// Send event
		this.sendEvent( 'automator/conditions-group/add-action', {
			detail: {
				actionID: actionID
			},
		} );
	}

	/**
	 * Removes an action from the array of actions of this conditions group
	 *
	 * @param {Integer} actionID The action ID
	 */
	removeAction( actionID ) {
		// Check if it has that action
		if ( ! this.hasAction( actionID ) ) {
			return;
		}
		
		// Update
		this.actions = this.actions.filter( ID => ID != actionID );

		// Send event
		this.sendEvent( 'automator/conditions-group/remove-action', {
			detail: {
				actionID: actionID
			},
		} );
	}

	/**
	 * Helper to send custom events
	 * 
	 * @param {String} type A string with the name of the event.
	 * @param {*} data An object with custom data
	 * @returns 
	 */
	sendEvent( type, data ) {
		// Check if the block has been rendered
		if ( ! this.$currentConditionsBlock ) {
			return;
		}

		this.$currentConditionsBlock.dispatchEvent( new CustomEvent( type, data ) );
	}

	/**
	 * Returns the node of the current conditions block tied to this conditions group
	 */
	get $currentConditionsBlock() {
		return getConditionsBlockNode( this.id );
	}

	/**
	 * Sets the mode
	 *
	 * @param {String} mode The mode. Either "any" or "all"
	 * @return {undefined}
	 */
	set mode( mode ) {
		// Check if the mode is valid
		if ( [ 'all', 'any' ].includes( mode ) ) {
			this.#_mode = mode;
		} else {
			// Set default mode
			this.#_mode = 'any';
		}
	}

	/**
	 * Returns the mode
	 *
	 * @return {String} The mode
	 */
	get mode() {
		return this.#_mode;
	}

	/**
	 * Adds the actions
	 *
	 * @param {Array} actions The actions IDs
	 * @return {undefined}
	 */
	set actions( actions ) {
		// Default values
		this.#_actions = [];

		try {
			// Iterate the actions
			actions.forEach( ( actionID ) => {
				// Make sure the action ID is an integer
				actionID = parseInt( actionID );

				if ( isNaN( actionID ) ) {
					return;
				}

				// Check if the action actually exists
				if ( ! isDefined( UncannyAutomator.recipe.items[ actionID ] ) ) {
					return;
				}

				// Add the action ID
				this.#_actions.push( actionID );
			} );
		} catch ( e ) {
			if ( UncannyAutomator._site.has_debug_enabled ) {
				// eslint-disable-next-line no-console
				console.error( "ConditionsGroup: Couldn't set the actions. Make sure the array is valid." );
			}
		}
	}

	/**
	 * Returns the actions
	 *
	 * @return {Array} The IDs
	 */
	get actions() {
		// Remove actions that no longer exist
		// Iterate actions
		this.#_actions = this.#_actions.filter( ( actionId ) => {
			// Check if the action exists
			return isDefined( UncannyAutomator.recipe.items[ actionId ] );
		} );

		return this.#_actions;
	}

	/**
	 * Sets the conditions
	 *
	 * @param {Array} conditionsList An array with conditions
	 * @return {undefined}
	 */
	set conditions( conditionsList ) {
		// Default value
		this.#_conditions = {};

		try {
			// Iterate conditions
			conditionsList.forEach( ( condition ) => {
				// Add condition
				this.addCondition( {
					conditionID: condition.id,
					integrationCode: condition.integration,
					conditionCode: condition.condition,
					fields: condition.fields,
					backup: condition.backup
				} );
			} );
		} catch ( e ) {
			if ( UncannyAutomator._site.has_debug_enabled ) {
				// eslint-disable-next-line no-console
				console.error( "ConditionsGroup: Couldn't set the conditions. Make sure the array is valid." );

				// eslint-disable-next-line no-console
				console.error( e );
			}
		}
	}

	/**
	 * Returns the conditions
	 *
	 * @return {Array} The conditions
	 */
	get conditions() {
		return this.#_conditions;
	}

	/**
	 * Returns an object with sanitazed data for the backend
	 *
	 * @return {Object} Sanitazed data
	 */
	get formattedForMeta() {
		// Default response
		let conditionsGroupForMeta = {};

		/**
		 * The backend expects
		 *
		 * {
		 * 	 actions: [], // Array with actions IDs
		 * 	 mode: 'any', // String, either "all" or "any"
		 * 	 conditions: [] // Array with conditions
		 * }
		 */
		conditionsGroupForMeta = {
			id: this.id,
			priority: this.priority,
			actions: this.actions,
			mode: this.mode,
			parent_id: this.parentID,
			conditions: [],
		};

		// Iterate conditions
		// eslint-disable-next-line no-unused-vars
		Object.entries( this.conditions ).forEach( ( [ conditionId, condition ] ) => {
			// Format the condition too
			conditionsGroupForMeta.conditions.push( condition.formattedForMeta );
		} );

		return conditionsGroupForMeta;
	}

	/**
	 * Get the priority. 
	 * If the conditions block has actions, this will be equal to the priority of the first action
	 * If it doesn't have conditions, it will check if it had a priority set before (before it was empty)
	 */
	get priority() {
		// Check if it has actions
		if ( ! this.hasActions ) {
			return this.#_priority;
		}

		// Get the lowest priority of the conditions block actions
		this.#_priority = UncannyAutomator.recipe.items[ this.actionWithLowestPriority ].menuOrder;

		return this.#_priority;
	}

	/**
	 * Manually sets the priority
	 * 
	 * @param {Integer} value The new priority 
	 */
	set priority( value ) {
		this.#_priority = value;
	}

	/**
	 * Returns the ID of the action with the lowest priority
	 * 
	 * @return {Integer} The ID of the action
	 */
	get actionWithLowestPriority() {
		let lowestPriority,
		    lowestPriorityAction;

		// Get the target actions
		// This will be used to decide if we should find the lowest priority in all actions, or only the instant ones
		let actions = this.hasInstantActions ? this.actionsByType.instant : this.actions;

		actions.forEach( ( actionID ) => {
			// Get the priority
			const actionPriority = UncannyAutomator.recipe.items[ actionID ].menuOrder;

			// Check if is lower than our current lowest priority
			// If the priority is not defined, just use the action priority (we can't compare it to anything else)
			// If it's defined, check if the action priority is less than our current lowest priority
			if ( 
				isEmpty( lowestPriority ) || ( actionPriority < lowestPriority )
			) {
				lowestPriority = actionPriority;
				lowestPriorityAction = actionID;

				// Stop here
				return;
			}
		} );

		return lowestPriorityAction;
	}

	/**
	 * Checks whether the condition group has at least one instant action
	 * 
	 * @return {Boolean} True if it has instant actions
	 */
	get hasInstantActions() {
		return this.actionsByType.instant.length > 0;
	}

	/**
	 * Checks whether the condition group has at least one delayed action
	 * 
	 * @return {Boolean} True if it has delayed actions
	 */
	get hasDelayedActions() {
		return this.actionsByType.delayed.length > 0;
	}

	/**
	 * Checks whether the condition group has at least one scheduled action
	 * 
	 * @return {Boolean} True if it has scheduled actions
	 */
	get hasScheduledActions() {
		return this.actionsByType.scheduled.length > 0;
	}

	/**
	 * Returns an object that groups actions by action type
	 * 
	 * @return {Object} The recipes, by type
	 */
	get actionsByType() {
		const type = {
			instant: [],
			delayed: [],
			delayedCustom: [],
			scheduled: []
		}

		this.actions.forEach( ( actionID ) => {
			// Get the action
			const action = UncannyAutomator.recipe.items[ actionID ];

			// Get the type
			if ( action.isScheduled ) {
				type.scheduled.push( actionID );
				return;
			}

			if ( action.isDelayed ) {
				type.delayed.push( actionID );
				return;
			}

			if ( action.isDelayedCustom ) {
				type.delayedCustom.push( actionID );
				return;
			}

			type.instant.push( actionID );
		} );

		return type;
	}

	/**
	 * Determine if the condition group should be sortable
	 *
	 * @return {Boolean} `true` if the conditions group can be sorted
	 */
	get isSortable() {
		return shouldBeSortable( {
			conditionsGroupID: this.id
		} );
	}

	/**
	 * Checks whether the conditions group has an action
	 *
	 * @param {Integer} actionId The action ID
	 * @return {Boolean}           True if it has it
	 */
	hasAction( actionId ) {
		return this.actions.includes( actionId );
	}

	/**
	 * Checks whether this conditions group has a condition
	 *
	 * @param {String} conditionId The unique condition ID (hash)
	 * @return {Boolean}             True if it has it
	 */
	hasCondition( conditionId ) {
		return isDefined( this.conditions[ conditionId ] );
	}

	/**
	 * Returns a condition given an ID
	 *
	 * @param {String} conditionId The unique condition ID (hash)
	 * @return {Condition}          The condition
	 */
	getCondition( conditionId ) {
		return this.conditions[ conditionId ];
	}

	/**
	 * Destroys this ConditionsGroup
	 *
	 * @return {undefined}
	 */
	destroy() {
		delete UncannyAutomator.recipe.conditions[ this.id ];
	}

	/**
	 * Evaluates if the condition has actions
	 *
	 * @return {Boolean} True if it has actions
	 */
	get hasActions() {
		return ! isEmpty( this.actions );
	}

	/**
	 * Evaluates if the condition has conditions
	 *
	 * @return {Boolean} True if it has conditions
	 */
	get hasConditions() {
		return ! isEmpty( this.conditions );
	}

	/**
	 * Returns the number of actions
	 *
	 * @return {Integer} The number of actions
	 */
	get numberOfActions() {
		return Object.keys( this.actions ).length;
	}

	/**
	 * Returns the number of conditions
	 *
	 * @return {Integer} The number of conditions
	 */
	get numberOfConditions() {
		return Object.keys( this.conditions ).length;
	}
}
